import { createContext, PropsWithChildren, useContext, useId } from 'react';

import { isWeb, withStaticProperties } from '@tamagui/core';

import { Box, BoxProps } from '../Box';
import { Button, ButtonProps } from '../Button';
import { SubmitHandlerProp } from './types';

type FormContextProps = SubmitHandlerProp & {
  /**
   * The id of the form
   */
  formId?: string;
};

// Using context so we can pass the onSubmit function to the form or button,
// depending on the platform
const FormContext = createContext<FormContextProps>({ onSubmit: () => {}, formId: '' });

type FormProps = PropsWithChildren<FormContextProps>;

const FormProvider = ({ onSubmit, formId, children }: FormProps) => {
  const backupId = useId();
  const id = formId || backupId;

  return <FormContext.Provider value={{ onSubmit, formId: id }}>{children}</FormContext.Provider>;
};

type FormComponentProps = Omit<BoxProps, 'tag'>;

const FormComponent = ({ children, ...props }: FormComponentProps) => {
  const { onSubmit, formId } = useContext(FormContext);
  return (
    <Box
      tag="form"
      id={formId}
      // @ts-expect-error onSubmit doesn't exist on BoxProps, but passing
      // the prop still works on web
      onSubmit={isWeb ? onSubmit : undefined}
      {...props}
    >
      {children}
    </Box>
  );
};

type FormButtonProps = Omit<ButtonProps, 'type' | 'onPress'>;

const FormButton = (props: FormButtonProps) => {
  const { onSubmit, formId } = useContext(FormContext);
  return (
    <Button
      type="submit"
      form={formId}
      // @ts-expect-error on native (types.native.ts) the type of onSubmit is
      // the same as onPress, but the default typing is for web (types.ts) where
      // it's typed differently but not being used
      onPress={isWeb ? undefined : onSubmit}
      {...props}
    />
  );
};

// Basic cross-platform Form component. Lets us place the `<button>` outside the
// `<form>` element if required. Currently only used internally.
const Form = withStaticProperties(FormProvider, {
  Form: FormComponent,
  Button: FormButton,
});

export { Form };
export type { FormProps, FormComponentProps, FormButtonProps };
